import { MEDIA_BATCH_SIZE, type PresentationMedia, type PresentationMediaResponse } from '../api/presentation-media';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { cleanPermalink } from '../../statistics/lib/top-videos';

/**
 * Ссылки на публикации → данные слайдов презентации.
 *
 * Цифры берутся с сервера (/api/presentation/media ищет пост среди наших
 * аккаунтов и спрашивает Graph API), превью перезаливается в Storage: ссылки
 * CDN Instagram протухают за сутки и не читаются браузером в canvas.
 *
 * Сеть подаётся снаружи, чтобы порядок, отбор и ошибки проверялись тестом.
 */

/** Надпись на бейдже слайда, как в макете. */
export type SlideBadge = 'Пост' | 'Reels';

export type PostSlide = {
  /** Ссылка для кнопки «Посмотреть» — без трекинговых хвостов. */
  url: string;
  badge: SlideBadge;
  username: string;
  /** Просмотры в Instagram (метрика views). */
  views: number;
  /** Охват (метрика reach). */
  reach: number;
  likes: number;
  comments: number;
  saved: number;
  /** Отправки; null — «нет данных», а не «никто не отправил». */
  shares: number | null;
  /** Среднее время просмотра рилса, мс; у постов — null. */
  avgWatchMs: number | null;
  /** Ниже — null, если Instagram метрику не отдал. */
  totalInteractions: number | null;
  /** Расчётом: взаимодействия − лайки − комментарии − отправки − сохранения. */
  reposts: number | null;
  profileVisits: number | null;
  follows: number | null;
  /** Общее время просмотра рилса, мс. */
  totalWatchMs: number | null;
  /** Пропуски в первые 3 секунды, %. */
  skipRate: number | null;
  /**
   * Превью: перезалитое в Storage, а если не вышло — исходная ссылка CDN
   * (её браузер достанет через прокси). Пустая строка — картинки нет вовсе.
   */
  imageUrl: string;
  publishedAt: string | null;
};

export type SlideError = { url: string; error: string };

/**
 * Ссылка на публикацию, по которой статистики нет: паблик не подключён к
 * InstaFarm или Instagram метрик не отдал. Такую публикацию добивают руками
 * — скриншотом статистики, — поэтому она возвращается отдельно от ссылок,
 * которые исправить нельзя (сторис, чужие адреса).
 */
export type MissingPost = { url: string; shortcode: string; reason: string };

export type BuildPostSlidesDeps = {
  /** Не больше MEDIA_BATCH_SIZE шорткодов за вызов. */
  fetchMedia: (shortcodes: string[]) => Promise<PresentationMediaResponse>;
  /** mediaId → постоянный адрес превью. Кого нет в ответе — берётся исходная ссылка. */
  rehostImages: (items: Array<{ id: string; thumbnailUrl: string }>) => Promise<Map<string, string>>;
  /**
   * mediaId → просмотры с таргета; их прибавляем к органике. Не задан —
   * слайд показывает только органику, как раньше.
   */
  fetchPaidViews?: (media: PresentationMedia[]) => Promise<Map<string, number>>;
};

export type BuildPostSlidesResult = {
  slides: PostSlide[];
  errors: SlideError[];
  missing: MissingPost[];
};

const STORIES_PATTERN = /instagram\.com\/stories\//i;

// У видео и рилсов media_url — это сам ролик, картинка лежит в thumbnail_url.
// У карусели, чей первый элемент — видео, media_url тоже ведёт на mp4.
function pickPreview(media: PresentationMedia): string {
  const candidates = [media.thumbnailUrl, media.mediaUrl];
  for (const url of candidates) {
    if (url && !/\.mp4(\?|$)/i.test(url)) return url;
  }
  return '';
}

/** Кнопка в PDF без протокола не откроется: «www.instagram.com/p/…» → https://. */
function buttonUrl(url: string): string {
  return cleanPermalink(/^https?:\/\//i.test(url) ? url : `https://${url}`);
}

function isVideo(media: PresentationMedia): boolean {
  return media.productType === 'REELS' || media.mediaType === 'VIDEO';
}

export async function buildPostSlides(
  urls: string[],
  deps: BuildPostSlidesDeps,
  onProgress: (message: string) => void = () => {},
): Promise<BuildPostSlidesResult> {
  const errors: SlideError[] = [];
  const missing: MissingPost[] = [];

  // Порядок слайдов — порядок ссылок во вводе; повтор одного поста выкидываем.
  const ordered: Array<{ url: string; shortcode: string }> = [];
  const seen = new Set<string>();
  for (const raw of urls) {
    const url = raw.trim();
    if (!url) continue;
    if (STORIES_PATTERN.test(url)) {
      errors.push({ url, error: 'Сторис пока не поддерживаются' });
      continue;
    }
    const shortcode = extractShortcode(url);
    if (!shortcode) {
      errors.push({ url, error: 'Это не ссылка на пост Instagram' });
      continue;
    }
    if (seen.has(shortcode)) continue;
    seen.add(shortcode);
    ordered.push({ url, shortcode });
  }

  if (ordered.length === 0) return { slides: [], errors, missing };

  const byShortcode = new Map<string, PresentationMedia>();
  const failed = new Map<string, string>();
  for (let i = 0; i < ordered.length; i += MEDIA_BATCH_SIZE) {
    const part = ordered.slice(i, i + MEDIA_BATCH_SIZE);
    onProgress(
      ordered.length > MEDIA_BATCH_SIZE
        ? `Статистика публикаций: ${i + part.length} из ${ordered.length}...`
        : 'Статистика публикаций...',
    );
    const res = await deps.fetchMedia(part.map((o) => o.shortcode));
    for (const m of res.media) byShortcode.set(m.shortcode, m);
    for (const e of res.errors) failed.set(e.shortcode, e.error);
  }

  const found: Array<{ url: string; media: PresentationMedia }> = [];
  for (const o of ordered) {
    const media = byShortcode.get(o.shortcode);
    if (!media) {
      missing.push({
        url: buttonUrl(o.url),
        shortcode: o.shortcode,
        reason: failed.get(o.shortcode) ?? 'Сервер не вернул статистику',
      });
    } else if (media.views === null && media.reach === null) {
      // Нули на слайде выглядели бы как провал публикации: такую публикацию
      // заполняют скриншотом из приложения.
      missing.push({
        url: buttonUrl(o.url),
        shortcode: o.shortcode,
        reason: `Instagram не отдал статистику: ${media.metricsError ?? 'нет данных'}`,
      });
    } else {
      found.push({ url: o.url, media });
    }
  }

  onProgress('Превью публикаций...');
  const toRehost = found
    .map(({ media }) => ({ id: media.mediaId, thumbnailUrl: pickPreview(media) }))
    .filter((i) => i.thumbnailUrl);
  // Перезаливка — только ради стабильной ссылки: её сбой не должен ронять отчёт.
  let images = new Map<string, string>();
  if (toRehost.length > 0) {
    try {
      images = await deps.rehostImages(toRehost);
    } catch (err) {
      console.warn('[Presentation] перезаливка превью не удалась, берём исходные ссылки:', err);
    }
  }

  // Просмотры с таргета: Instagram их не отдаёт, они живут в рекламном
  // кабинете. Сбой этого запроса не должен ронять отчёт — отчёт с одной
  // органикой полезнее, чем упавшая кнопка.
  let paidViews = new Map<string, number>();
  if (deps.fetchPaidViews) {
    onProgress('Просмотры с таргета...');
    try {
      paidViews = await deps.fetchPaidViews(found.map(({ media }) => media));
    } catch (err) {
      console.warn('[Presentation] реклама не подтянулась, остаётся органика:', err);
    }
  }

  const slides = found.map(({ url, media: m }): PostSlide => {
    const video = isVideo(m);
    return {
      url: buttonUrl(url),
      badge: video ? 'Reels' : 'Пост',
      username: m.username,
      // Органика плюс таргет одним числом: на слайде стоит «Просмотры», и
      // клиенту нужен итог. Охват так не складывают — аудитории пересекаются.
      views: (m.views ?? 0) + (paidViews.get(m.mediaId) ?? 0),
      reach: m.reach ?? 0,
      likes: m.likes ?? 0,
      comments: m.comments ?? 0,
      saved: m.saved ?? 0,
      shares: m.shares,
      avgWatchMs: video && m.avgWatchMs ? m.avgWatchMs : null,
      totalInteractions: m.totalInteractions,
      reposts: m.reposts,
      profileVisits: video ? null : m.profileVisits,
      follows: video ? null : m.follows,
      totalWatchMs: video ? m.totalWatchMs : null,
      skipRate: video ? m.skipRate : null,
      imageUrl: images.get(m.mediaId) || pickPreview(m),
      publishedAt: m.publishedAt,
    };
  });

  return { slides, errors, missing };
}
